import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  User,
  Edit3,
  Trophy,
  Target,
  TrendingUp,
  Award,
  Check,
  X,
  Swords
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { updateUserProfile } from '../services/auth.service';
import { migrateUserStats } from '../services/migrateStats';
import { UserRank } from '../types';

// Avatar options (using emoji representation)
const AVATARS = [
  '👨‍💼', '👩‍💼', '👨‍🎓', '👩‍🎓', '👨‍🏫',
  '👩‍🏫', '🧙‍♂️', '🧙‍♀️', '👼', '🦁'
];

// Rank configuration
const RANK_CONFIG: Record<UserRank, { min: number; max: number; color: string; description: string }> = {
  Seeker: { min: 0, max: 999, color: '#A7A9BE', description: 'Just starting your journey' },
  Disciple: { min: 1000, max: 4999, color: '#4CC9F0', description: 'Learning and growing' },
  Scholar: { min: 5000, max: 14999, color: '#06D6A0', description: 'Knowledgeable student' },
  Prophet: { min: 15000, max: 49999, color: '#FFB703', description: 'Wise teacher' },
  Master: { min: 50000, max: Infinity, color: '#FFD700', description: 'Biblical expert' },
};

const ProfileContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xl};
  padding-top: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.md};
    padding-top: 60px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  display: grid;
  grid-template-columns: 1fr 1.2fr 1.2fr;
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const ProfileCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const AvatarSection = styled.div`
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const AvatarDisplay = styled.div<{ $rankColor: string }>`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background};
  border: 4px solid ${({ $rankColor }) => $rankColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 0 20px ${({ $rankColor }) => `${$rankColor}33`};

  &:hover {
    transform: scale(1.05);
  }
`;

const EditAvatarButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text};
  border: 3px solid ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    transform: scale(1.1);
  }
`;

const AvatarGrid = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
  z-index: 10;
  box-shadow: ${({ theme }) => theme.shadows.lg};
`;

const AvatarOption = styled.button<{ $selected: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary : theme.colors.background};
  border: 2px solid ${({ theme, $selected }) =>
    $selected ? theme.colors.accent : theme.colors.primary};
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const UsernameSection = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const UsernameDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Username = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.75rem;
  margin: 0;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const UsernameInput = styled.input`
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  font-size: 1.2rem;
  text-align: center;
  width: 100%;
  max-width: 250px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const InputActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const ActionButton = styled.button<{ $variant: 'success' | 'cancel' }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ theme, $variant }) =>
    $variant === 'success' ? theme.colors.success : theme.colors.error};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const RankBadge = styled.div<{ $color: string }>`
  background: linear-gradient(135deg, ${({ $color }) => $color}, ${({ $color }) => `${$color}CC`});
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  box-shadow: 0 4px 12px ${({ $color }) => `${$color}44`};
`;

const XPSection = styled.div`
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const XPDisplay = styled.div`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const XPText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

const XPNumber = styled.div`
  color: ${({ theme }) => theme.colors.accent};
  font-size: 2rem;
  font-weight: 700;
`;

const XPBar = styled.div`
  width: 100%;
  height: 12px;
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  overflow: hidden;
  position: relative;
`;

const XPProgress = styled(motion.div)<{ $color: string }>`
  height: 100%;
  background: linear-gradient(90deg, ${({ $color }) => $color}, ${({ $color }) => `${$color}CC`});
  border-radius: ${({ theme }) => theme.borderRadius.full};
  box-shadow: 0 0 10px ${({ $color }) => `${$color}88`};
`;

const NextRankText = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.85rem;
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const StatsCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: ${({ theme }) => theme.shadows.lg};

  @media (max-width: 767px) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const StatsTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.5rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border-left: 4px solid ${({ theme }) => theme.colors.primary};
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const StatValue = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: 2rem;
  font-weight: 700;
`;

const StatSubtext = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.85rem;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const ProfileScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Migrate old stats on component mount
  useEffect(() => {
    if (currentUser) {
      migrateUserStats(currentUser.uid).catch(err => {
        console.error('Migration error:', err);
      });
    }
  }, [currentUser]);

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  const rankConfig = RANK_CONFIG[currentUser.rank];
  const xpProgress = currentUser.rank === 'Master'
    ? 100
    : ((currentUser.xp - rankConfig.min) / (rankConfig.max - rankConfig.min + 1)) * 100;

  const handleUsernameEdit = () => {
    setNewUsername(currentUser.displayName);
    setIsEditingUsername(true);
  };

  const handleUsernameSave = async () => {
    if (newUsername.trim() && newUsername !== currentUser.displayName) {
      setIsLoading(true);
      try {
        await updateUserProfile(currentUser.uid, { displayName: newUsername.trim() });
        // Reload page to update auth context
        window.location.reload();
      } catch (error) {
        console.error('Failed to update username:', error);
        alert('Failed to update username. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
    setIsEditingUsername(false);
  };

  const handleUsernameCancel = () => {
    setIsEditingUsername(false);
    setNewUsername('');
  };

  const handleAvatarSelect = async (avatarIndex: number) => {
    if (avatarIndex !== currentUser.avatar) {
      setIsLoading(true);
      try {
        await updateUserProfile(currentUser.uid, { avatar: avatarIndex });
        // Reload page to update auth context
        window.location.reload();
      } catch (error) {
        console.error('Failed to update avatar:', error);
        alert('Failed to update avatar. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
    setShowAvatarPicker(false);
  };

  const getNextRank = (): { rank: UserRank; xpNeeded: number } | null => {
    if (currentUser.rank === 'Master') return null;

    const ranks: UserRank[] = ['Seeker', 'Disciple', 'Scholar', 'Prophet', 'Master'];
    const currentIndex = ranks.indexOf(currentUser.rank);
    const nextRank = ranks[currentIndex + 1];
    const nextRankConfig = RANK_CONFIG[nextRank];

    return {
      rank: nextRank,
      xpNeeded: nextRankConfig.min - currentUser.xp,
    };
  };

  const nextRank = getNextRank();

  return (
    <ProfileContainer>
      <ContentWrapper>
        {/* Left Column - Profile Card */}
        <ProfileCard
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <AvatarSection>
            <AvatarDisplay
              $rankColor={rankConfig.color}
              onClick={() => setShowAvatarPicker(!showAvatarPicker)}
            >
              {AVATARS[currentUser.avatar]}
            </AvatarDisplay>
            <EditAvatarButton onClick={() => setShowAvatarPicker(!showAvatarPicker)}>
              <Edit3 size={16} />
            </EditAvatarButton>

            <AnimatePresence>
              {showAvatarPicker && (
                <AvatarGrid
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                >
                  {AVATARS.map((avatar, index) => (
                    <AvatarOption
                      key={index}
                      $selected={index === currentUser.avatar}
                      onClick={() => handleAvatarSelect(index)}
                      disabled={isLoading}
                    >
                      {avatar}
                    </AvatarOption>
                  ))}
                </AvatarGrid>
              )}
            </AnimatePresence>
          </AvatarSection>

          <UsernameSection>
            {isEditingUsername ? (
              <>
                <UsernameInput
                  type="text"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  maxLength={20}
                  autoFocus
                  disabled={isLoading}
                />
                <InputActions>
                  <ActionButton
                    $variant="success"
                    onClick={handleUsernameSave}
                    disabled={isLoading}
                  >
                    <Check size={20} />
                  </ActionButton>
                  <ActionButton
                    $variant="cancel"
                    onClick={handleUsernameCancel}
                    disabled={isLoading}
                  >
                    <X size={20} />
                  </ActionButton>
                </InputActions>
              </>
            ) : (
              <UsernameDisplay>
                <Username>{currentUser.displayName}</Username>
                <EditButton onClick={handleUsernameEdit}>
                  <Edit3 size={18} />
                </EditButton>
              </UsernameDisplay>
            )}

            <RankBadge $color={rankConfig.color}>
              <Award size={16} />
              {currentUser.rank}
            </RankBadge>
          </UsernameSection>

          <XPSection>
            <XPDisplay>
              <XPText>Experience Points</XPText>
              <XPNumber>{currentUser.xp.toLocaleString()}</XPNumber>
            </XPDisplay>

            <XPBar>
              <XPProgress
                $color={rankConfig.color}
                initial={{ width: 0 }}
                animate={{ width: `${xpProgress}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
              />
            </XPBar>

            {nextRank && (
              <NextRankText>
                {nextRank.xpNeeded.toLocaleString()} XP until {nextRank.rank}
              </NextRankText>
            )}
          </XPSection>
        </ProfileCard>

        {/* Right Column - Stats Card */}
        <StatsCard
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <StatsTitle>
            <Trophy size={24} />
            Single Player Stats
          </StatsTitle>

          <StatsGrid>
            <StatItem>
              <StatLabel>
                <Trophy size={16} />
                Games Played
              </StatLabel>
              <StatValue>{currentUser.stats.gamesPlayed}</StatValue>
            </StatItem>

            <StatItem>
              <StatLabel>
                <Award size={16} />
                Best Score
              </StatLabel>
              <StatValue>{currentUser.stats.bestScore}</StatValue>
            </StatItem>

            <StatItem>
              <StatLabel>
                <TrendingUp size={16} />
                Average Score
              </StatLabel>
              <StatValue>{Math.round(currentUser.stats.averageScore)}</StatValue>
            </StatItem>

            <StatItem>
              <StatLabel>
                <Target size={16} />
                Accuracy
              </StatLabel>
              <StatValue>{Math.round(currentUser.stats.accuracyRate)}%</StatValue>
            </StatItem>

            <StatItem>
              <StatLabel>
                <TrendingUp size={16} />
                Current Streak
              </StatLabel>
              <StatValue>{currentUser.stats.currentStreak}</StatValue>
              <StatSubtext>Longest: {currentUser.stats.longestStreak}</StatSubtext>
            </StatItem>

            <StatItem>
              <StatLabel>
                <User size={16} />
                Favorite Category
              </StatLabel>
              <StatValue style={{ fontSize: '1.2rem' }}>
                {currentUser.stats.favoriteCategory || 'None yet'}
              </StatValue>
            </StatItem>
          </StatsGrid>
        </StatsCard>

        {/* Multiplayer Stats Card */}
        <StatsCard
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <StatsTitle>
            <Swords size={24} />
            Multiplayer Stats
          </StatsTitle>

          <StatsGrid>
            <StatItem>
              <StatLabel>
                <Trophy size={16} />
                Games Played
              </StatLabel>
              <StatValue>{currentUser.multiplayerStats?.gamesPlayed || 0}</StatValue>
            </StatItem>

            <StatItem>
              <StatLabel>
                <Award size={16} />
                Wins
              </StatLabel>
              <StatValue>{currentUser.multiplayerStats?.wins || 0}</StatValue>
            </StatItem>

            <StatItem>
              <StatLabel>
                <Target size={16} />
                Losses
              </StatLabel>
              <StatValue>{currentUser.multiplayerStats?.losses || 0}</StatValue>
            </StatItem>

            <StatItem>
              <StatLabel>
                <User size={16} />
                Draws
              </StatLabel>
              <StatValue>{currentUser.multiplayerStats?.draws || 0}</StatValue>
            </StatItem>

            <StatItem>
              <StatLabel>
                <TrendingUp size={16} />
                Win Rate
              </StatLabel>
              <StatValue>{currentUser.multiplayerStats?.winRate || 0}%</StatValue>
            </StatItem>

            <StatItem>
              <StatLabel>
                <Trophy size={16} />
                Best Score
              </StatLabel>
              <StatValue>{currentUser.multiplayerStats?.bestScore || 0}</StatValue>
            </StatItem>

            <StatItem>
              <StatLabel>
                <TrendingUp size={16} />
                Current Streak
              </StatLabel>
              <StatValue>{currentUser.multiplayerStats?.currentStreak || 0}</StatValue>
              <StatSubtext>Longest: {currentUser.multiplayerStats?.longestStreak || 0}</StatSubtext>
            </StatItem>

            <StatItem>
              <StatLabel>
                <Award size={16} />
                Average Score
              </StatLabel>
              <StatValue>{currentUser.multiplayerStats?.averageScore || 0}</StatValue>
            </StatItem>
          </StatsGrid>
        </StatsCard>
      </ContentWrapper>
    </ProfileContainer>
  );
};

export default ProfileScreen;
